import React, {Component, Fragment} from 'react';
import {inject, observer} from 'mobx-react';
import {action, observable, runInAction} from 'mobx';
import Joyride from 'react-joyride';
import debounce from 'lodash/debounce';
import {wikiApi, wikiUrl} from '../constants/constants';
import {ellipsisString} from '../utils/commonUtility';
import {isDesktop} from '../utils/screenUtility';


export function withWiki(ComposedComponent) {
    return @inject('looperStore') @observer class extends Component {
        @observable showTooltip = false;
        @observable isModifiedDescription = false;
        @observable tooltipContent = null;
        @observable tooltipTarget = null;
        @observable musicoDictionary = require('../dictionary/data') || [];

        componentDidMount() {
            this.debounceOver = debounce(this.handleMouseOver, 250);
            if (isDesktop()) {
                document.addEventListener('mouseenter', this.debounceOver, true);
                document.addEventListener('mouseleave', this.handleMouseLeave, true);
            } else {
                document.addEventListener('click', this.handleClick, true);
            }
        }

        componentWillUnmount() {
            if (isDesktop()) {
                document.removeEventListener('mouseenter', this.debounceOver, true);
                document.removeEventListener('mouseleave', this.handleMouseLeave, true);
            } else {
                document.removeEventListener('click', this.handleClick, true);
            }
        }

        @action handleClick = (e) => {
            this.showTooltip = false;
            if (this.checkTarget(e.target)) {
                if (e.target.classList.contains('taped')) {
                    e.target.classList.remove('taped');
                } else {
                    e.preventDefault();
                    e.target.classList.add('taped');
                    this.getWikiInfo(e.target);
                }
            } else {
                const tapedLinks = document.querySelectorAll('.wiki.taped');
                if (tapedLinks.length) {
                    [...tapedLinks].forEach(item => item.classList.remove('taped'));
                }
            }
        };

        handleMouseOver = ({target}) => {
            if (this.checkTarget(target)) {
                this.getWikiInfo(target);
            }
        };

        @action getWikiInfo = (link) => {
            this.tooltipTarget = `[data-id="${link.dataset.id}"]`;
            const wikiItem = this.findItemByKey(link.dataset.url);
            if (wikiItem.content) {
                this.tooltipContent = wikiItem.content;
                setTimeout(action(() => {
                    this.showTooltip = true;
                }), 100);
            } else {
                this.fetchWikiData(link.dataset.url, wikiItem);
            }
        };

        @action handleMouseLeave = ({target}) => {
            if (this.checkTarget(target)) {
                this.debounceOver.cancel();
                this.showTooltip = false;
            }
        };

        fetchWikiData = async (url, dictionaryRef) => {
            try {
                const response = await fetch(`${wikiApi}${url}`);
                const result = await response.json();
                const content = result.extract_html;
                if (content) {
                    runInAction(() => {
                        this.tooltipContent = ellipsisString(content, 600);
                        this.showTooltip = true;
                        dictionaryRef.content = ellipsisString(content, 600);
                    });
                }
            } catch (err) {
                console.warn(err.message);
            }
        };

        checkTarget = target => (
            target.classList && target.classList.contains('wiki')
        );

        findItemByKey = key => (
            this.musicoDictionary.find(item => item.wikiKey === key) || {}
        );

        @action replaceString = (string) => {
            let replacements = [];
            let i = 0;

            // first find all the links in the string and replace with ~~~0~~~
            let linkMatcher;
            let links = [];
            do {
                linkMatcher = string.match(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/i);
                if (linkMatcher) {
                    string = string.replace(linkMatcher[0], `~~~${i}~~~`);
                    links.push({
                        index: i,
                        word: linkMatcher[0]
                    });
                    i++;
                }
            } while (linkMatcher);

            // replace all knows words with ___0___
            i=0;
            this.musicoDictionary.forEach((item) => {
                const regexp = new RegExp(`\\b${item.word}s\?\\b`, 'i');
                let matches = string.match(regexp);
                while (matches) {
                    replacements.push({
                        index: i,
                        word: `<a target="_blank" href="${wikiUrl}${item.wikiKey}" data-url="${item.wikiKey}" class="wiki">${matches[0]}</a>`
                    });
                    string = string.replace(regexp, `___${i}___`);
                    i++;
                    matches = string.match(regexp);
                }
            });

            // replace all ___0___ (known words) with the wiki link
            replacements.forEach((replacement, i) => {
                string = string.replace(new RegExp(`___${i}___`, 'g'), replacement.word);
            });

            // replace all ~~~0~~~ with the links extracted before - the link were remove to prevent replacing known words inside links
            links.forEach((link, i) => {
                string = string.replace(new RegExp(`~~~${i}~~~`, 'g'), link.word);
            });
            setTimeout(() => {
                runInAction(() => {
                    this.isModifiedDescription = true;
                });
            }, 0);
            return string;
        };

        renderTooltip = ({step, tooltipProps}) => (
            <div className="wiki-tooltip" {...tooltipProps} dangerouslySetInnerHTML={{__html: step.content}}/>
        );

        render() {
            return (
                <Fragment>
                    <ComposedComponent
                        {...this.props}
                        makeWikiString={this.replaceString}
                    />
                    <Joyride
                        run={this.showTooltip}
                        tooltipComponent={this.renderTooltip}
                        spotlightPadding={5}
                        disableScrolling={true}
                        disableScrollParentFix={true}
                        steps={[{
                            target: this.tooltipTarget,
                            content: this.tooltipContent,
                            disableBeacon: true,
                            disableOverlay: true,
                            offset: 0
                        }]}
                        styles={{
                            options: {
                                arrowColor: '#6b93cd'
                            }
                        }}
                        floaterProps={{
                            styles: {
                                arrow: {
                                    length: 8,
                                    spread: 20
                                }
                            }
                        }}
                    />
                </Fragment>
            );
        }
    };
}
